import React, { forwardRef, useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';

import { isLocalURL } from '$utilities/browser';

/* ========================================================================= */
/* Component(s) */
/* ========================================================================= */

/**
 * @typedef {PropTypes.InferProps<typeof propTypes>} Props
 * @typedef {Object} ExtendedProps
 * @prop {('a'|'button'|'div')} [as=button] DOM element
 *
 * 1. Generates default attributes based on the button type and link location.
 */
const Button = forwardRef((
    /**
     * @type {Omit<Props, 'as' | 'look' | 'size'> & ExtendedProps}
     */
    {
        as: ComponentTag = 'button',
        children,
        className = '',
        disabled,
        renderIcon,
        ...remainingProps
    },
    /**@type {React.ForwardedRef<(HTMLButtonElement | HTMLAnchorElement | HTMLDivElement)>} */
    ref
) => {
    const [isLabelExiting, setIsLabelExiting] = useState(false);
    const exitTimerRef = useRef(/** @type {ReturnType<typeof setTimeout> | undefined} */ (undefined));

    useEffect(() => () => clearTimeout(exitTimerRef.current), []);

    /* Cast to any so we can pull out event handlers not declared in the typed Props */
    const { onMouseEnter: externalOnMouseEnter, onMouseLeave: externalOnMouseLeave, ...restProps } = /** @type {any} */ (remainingProps);

    const handleMouseEnter = useCallback((/** @type {React.MouseEvent} */ e) => {
        clearTimeout(exitTimerRef.current);
        setIsLabelExiting(false);
        externalOnMouseEnter?.(e);
    }, [externalOnMouseEnter]);

    const handleMouseLeave = useCallback((/** @type {React.MouseEvent} */ e) => {
        setIsLabelExiting(true);
        exitTimerRef.current = setTimeout(() => setIsLabelExiting(false), 700);
        externalOnMouseLeave?.(e);
    }, [externalOnMouseLeave]);

    const buttonClasses = [];
    const defaultAttributes = {};

    /* [1] */
    switch (ComponentTag) {
    case 'a':
        if (restProps.href) {
            if (!isLocalURL(restProps.href)) {
                defaultAttributes.rel = 'noopener noreferrer';
                defaultAttributes.target = '_blank';
            }
        } else {
            if(!restProps.href) {
                console.warn('Missing href attribute on <Button> component');
            }
        }
        break;

    case 'button':
        defaultAttributes.type = 'button';
        break;
    }

    buttonClasses.push(className);

    if (disabled) {
        buttonClasses.push('muted');
    } else {
        buttonClasses.push('cursor-pointer');
    }

    if (isLabelExiting) {
        buttonClasses.push('is-label-exiting');
    }

    return (
        <ComponentTag
            className={buttonClasses.join(' ')}
            disabled={disabled}
            {...defaultAttributes}
            {...restProps}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
            ref={ref}>
            {children}

            {renderIcon && (
                <span
                    aria-hidden={true}
                    className={`${children ? ' ml-5' : ''}`}>
                    {renderIcon}
                </span>
            )}
        </ComponentTag>
    );
});

/* ========================================================================= */
/* Typing and export(s) */
/* ========================================================================= */

const propTypes = {
    active: PropTypes.bool,
    as: PropTypes.oneOf(['a', 'button', 'div']),
    href: PropTypes.string,
    onClick: PropTypes.func,
    children: PropTypes.oneOfType([
        PropTypes.array,
        PropTypes.element,
        PropTypes.string
    ]),
    className: PropTypes.string,
    disabled: PropTypes.bool,
    renderIcon: PropTypes.oneOfType([
        PropTypes.array,
        PropTypes.element,
        PropTypes.string
    ]),
};

Button.propTypes = propTypes;
Button.displayName = 'Button';

export default Button;
